import { motion } from "framer-motion";
import microsoftLogo from "@/assets/ms-icons/microsoft.svg";
import outlookIcon from "@/assets/ms-icons/outlook.svg";
import onedriveIcon from "@/assets/ms-icons/onedrive.svg";
import sharepointIcon from "@/assets/ms-icons/sharepoint.svg";
import teamsIcon from "@/assets/ms-icons/teams.svg";
import defenderIcon from "@/assets/ms-icons/defender.svg";
import authenticatorIcon from "@/assets/ms-icons/authenticator.png";
import {
  Settings,
  Globe,
  Shield,
  DatabaseBackup,
  MonitorSmartphone,
  Phone,
  RefreshCw,
  Eye,
  KeyRound,
  Stamp,
  ArrowRight,
  CheckCircle2,
  BadgeCheck,
  HeadphonesIcon,
  Ban,
  FileCheck,
  Monitor,
  Apple,
  Laptop,
  Shuffle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import keeperLogo from "@/assets/logo-keeper.png";

const usps = [
  {
    icon: outlookIcon,
    title: "Zakelijke e-mail met Exchange Online",
    text: "Altijd en overal bij je mail, agenda en contacten. Automatisch gesynchroniseerd op al je apparaten. Inclusief sterke bescherming tegen spam en phishing.",
    color: "bg-hero-gradient",
  },
  {
    icon: onedriveIcon,
    title: "Veilige opslag met OneDrive",
    text: "Minimaal 1 TB persoonlijke cloudopslag per gebruiker. Automatische versiegeschiedenis zodat je nooit meer werk kwijtraakt. Bestanden delen met collega's of klanten? Zo geregeld.",
    color: "bg-rinqel-blue-gradient",
  },
  {
    icon: sharepointIcon,
    title: "Kennisdeling via SharePoint",
    text: "Eén centrale plek voor al je bedrijfsdocumenten, processen en kennis. Bouw eenvoudig afdelingssites — zonder technische kennis. Altijd overzichtelijk en goed beveiligd.",
    color: "bg-rinqel-green-gradient",
  },
  {
    icon: teamsIcon,
    title: "Samenwerken in Microsoft Teams",
    text: "Chatten, bellen en videobellen in één app. Elk team krijgt automatisch een eigen opslagplek voor bestanden. Team-eigenaren beheren zelf wie er toegang heeft — handig voor afdelingen als Directie, Administratie of Algemeen.",
    color: "bg-hero-gradient",
  },
  {
    icon: defenderIcon,
    title: "Veiligheid & compliance",
    text: "Ingebouwde beveiliging met tweestapsverificatie, versleuteling van je gegevens en bescherming tegen cyberdreigingen. Voldoet aan de AVG/GDPR-eisen zodat je altijd compliant bent.",
    color: "bg-rinqel-blue-gradient",
  },
  {
    icon: Settings,
    title: "Eenvoudig beheer & schaalbaarheid",
    text: "Alles wordt centraal beheerd vanuit de cloud. Geen gedoe met lokale servers. Groeit je bedrijf? Dan groeit je werkplek gewoon mee. Updates worden automatisch uitgerold.",
    color: "bg-rinqel-green-gradient",
  },
  {
    icon: Globe,
    title: "Altijd en overal werken",
    text: "Op kantoor, thuis of onderweg — je hebt altijd toegang tot je bestanden en apps. Via je laptop, tablet of telefoon. Alles wordt realtime gesynchroniseerd.",
    color: "bg-hero-gradient",
  },
  {
    icon: authenticatorIcon,
    title: "Multi-Factor Authenticatie (MFA)",
    text: "Elke gebruiker logt in met een extra verificatiestap via de Microsoft Authenticator-app. Zelfs als een wachtwoord uitlekt, komt niemand zomaar je omgeving binnen. Door Rinqel standaard ingeschakeld en beheerd.",
    color: "bg-rinqel-blue-gradient",
  },
];

const beheerPunten = [
  {
    icon: Eye,
    text: "Proactief monitoren en beheren op afstand",
  },
  {
    icon: RefreshCw,
    text: "Automatisch updates en patches uitrollen",
  },
  {
    icon: Shield,
    text: "Problemen oplossen voordat je ze merkt",
  },
  {
    icon: Phone,
    text: "Geen ticketsysteem — gewoon bellen",
  },
];

const WerkplekContent = () => {
  return (
    <>
      {/* Sectie 1 — Intro */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Alles wat je nodig hebt om{" "}
              <span className="text-gradient">overal productief te werken</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Microsoft 365 is het fundament van jouw digitale werkplek. E-mail, opslag, samenwerken en beveiliging — het zit er allemaal in.{" "}
              <span className="font-semibold text-foreground">Rinqel</span> beheert alles voor je, zodat jij je kunt focussen op waar je goed in bent: ondernemen.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Sectie 1b — Windows & macOS */}
      <section className="bg-muted/40 py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Windows én macOS?{" "}
              <span className="text-gradient">Wij beheren beide</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Of je nu met Microsoft Windows of Apple macOS werkt — het maakt voor ons niet uit. Binnen de Managed Werkplek worden alle apparaten via Microsoft Intune automatisch ingericht, beveiligd en beheerd. Zelfde kwaliteit, zelfde gemak.
            </p>
          </motion.div>

          <div className="mx-auto mt-10 grid max-w-3xl gap-6 sm:grid-cols-2">
            <motion.div
              className="flex flex-col items-center rounded-xl border border-border bg-card p-6 shadow-card text-center transition-shadow hover:shadow-card-hover"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-rinqel-blue-gradient text-primary-foreground">
                <img loading="lazy" decoding="async" src={microsoftLogo} alt="Microsoft" className="h-7 w-7" />
              </div>
              <h3 className="text-lg font-bold">Microsoft Windows</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Windows-laptops en -pc's worden automatisch ingericht met alle apps, beveiliging en instellingen. Inloggen en aan de slag — binnen een paar minuten.
              </p>
              <Button variant="link" className="mt-3 px-0" asChild>
                <Link to="/diensten/werkplek/windows">
                  Meer over Windows beheer
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>

            <motion.div
              className="flex flex-col items-center rounded-xl border border-border bg-card p-6 shadow-card text-center transition-shadow hover:shadow-card-hover"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.08 }}
            >
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-hero-gradient text-primary-foreground">
                <svg className="h-7 w-7" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
              </div>
              <h3 className="text-lg font-bold">Apple macOS</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Macs worden via Apple Business Manager en Intune volledig automatisch ingericht. Eigendom blijft 100% bij het bedrijf — altijd.
              </p>
              <Button variant="link" className="mt-3 px-0" asChild>
                <Link to="/diensten/werkplek/macos">
                  Meer over macOS beheer
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
          </div>

          <motion.div
            className="mx-auto mt-10 max-w-2xl rounded-xl border border-border bg-card p-6 shadow-card text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.16 }}
          >
            <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-lg bg-rinqel-green-gradient text-primary-foreground">
              <Shuffle className="h-5 w-5" />
            </div>
            <h3 className="text-lg font-bold">Mix & match? Geen probleem.</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Binnen één bedrijf kunnen medewerkers zelf kiezen: Windows of macOS. Op basis van voorkeur, werkzaamheden of functie. Wij beheren alles vanuit één platform — ongeacht de mix.
            </p>
          </motion.div>

          <motion.p
            className="mx-auto mt-6 max-w-2xl text-center text-sm text-muted-foreground"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Beide platformen kunnen op afstand worden gewist, waardoor alle bedrijfsdata veilig wordt verwijderd bij verlies, diefstal of uitdiensttreding.
          </motion.p>
        </div>
      </section>

      {/* Sectie 2 — USP-kaarten */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.h2
            className="mb-12 text-center text-2xl font-bold md:text-3xl"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            De voordelen van{" "}
            <span className="text-gradient">Microsoft 365</span>
          </motion.h2>

          <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-2">
            {usps.map((usp, i) => {
              const Icon = usp.icon;
              return (
                <motion.div
                  key={usp.title}
                  className="flex gap-4 rounded-xl border border-border bg-card p-6 shadow-card transition-shadow hover:shadow-card-hover"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                >
                  {typeof usp.icon === 'string'
                    ? <img loading="lazy" decoding="async" src={usp.icon} alt="" className="h-8 w-8 shrink-0" />
                    : (
                      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${usp.color} text-primary-foreground`}>
                        <Icon className="h-5 w-5" />
                      </div>
                    )
                  }
                  <div>
                    <h3 className="font-semibold">{usp.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {usp.text}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sectie 3 — Back-ups */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl rounded-2xl border border-secondary/20 bg-gradient-to-br from-secondary/5 to-secondary/10 p-8 md:p-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-rinqel-blue-gradient text-primary-foreground">
              <DatabaseBackup className="h-7 w-7" />
            </div>
            <h2 className="text-2xl font-bold md:text-3xl">
              Back-ups? <span className="text-gradient">Altijd inbegrepen</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Wij maken doorlopend online maar offsite back-ups van alle mailboxen, OneDrive en SharePoint.
              Mocht er ooit iets misgaan, dan staat je data veilig bij ons klaar. Zonder extra kosten — bij al onze werkplekpakketten inbegrepen.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Sectie 4 — Beheer door Rinqel */}
      <section className="bg-muted/40 py-16 md:py-20">
        <div className="container">
          <div className="mx-auto max-w-3xl">
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-rinqel-green-gradient text-primary-foreground">
                <MonitorSmartphone className="h-7 w-7" />
              </div>
              <h2 className="text-2xl font-bold md:text-3xl">
                Beheer door{" "}
                <span className="text-gradient">Rinqel</span>
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                Wij houden jouw Microsoft-omgeving proactief up-to-date met gespecialiseerde beheersoftware.
                Zo draait alles altijd soepel — zonder dat jij er iets van merkt.
              </p>
            </motion.div>

            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              {beheerPunten.map((punt, i) => {
                const Icon = punt.icon;
                return (
                  <motion.div
                    key={punt.text}
                    className="flex items-center gap-3 rounded-xl border border-border bg-card p-5 shadow-card"
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-rinqel-green/10">
                      <Icon className="h-5 w-5 text-rinqel-green" />
                    </div>
                    <p className="text-sm font-medium">{punt.text}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Sectie 5 — Optionele diensten */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.h2
            className="mb-12 text-center text-2xl font-bold md:text-3xl"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Optioneel:{" "}
            <span className="text-gradient">nog meer gemak</span>
          </motion.h2>

          <div className="mx-auto grid max-w-4xl gap-6 md:grid-cols-2">
            {/* Keeper */}
            <motion.div
              className="flex flex-col rounded-xl border border-border bg-card p-6 shadow-card transition-shadow hover:shadow-card-hover"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              <div className="mb-4 flex items-center gap-3">
                <img loading="lazy" decoding="async" src={keeperLogo} alt="Keeper logo" className="h-11 w-11 object-contain shrink-0" />
                <h3 className="text-lg font-bold">Wachtwoordmanager — Keeper</h3>
              </div>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                Centraal wachtwoordbeheer voor je hele organisatie. Medewerkers loggen veilig in met Single Sign-On via Microsoft.
                Nieuwe collega erbij? Toegang in een paar klikken. Iemand vertrekt? Direct alles afgesloten. Veilig en overzichtelijk.
              </p>
              <div className="mt-4 space-y-1.5">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-rinqel-green" />
                  <span className="text-xs text-muted-foreground">SSO met Microsoft · Veilig on-/offboarding</span>
                </div>
                <p className="text-sm font-semibold">€ 6,00 <span className="text-xs font-normal text-muted-foreground">per gebruiker per maand</span></p>
              </div>
              <Button variant="link" className="mt-3 justify-start px-0" asChild>
                <Link to="/diensten/werkplek/keeper">
                  Meer over Keeper
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>

            {/* E-mailhandtekeningen */}
            <motion.div
              className="flex flex-col rounded-xl border border-border bg-card p-6 shadow-card transition-shadow hover:shadow-card-hover"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.08 }}
            >
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-rinqel-blue-gradient text-primary-foreground">
                  <Stamp className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-bold">Centrale e-mailhandtekeningen</h3>
              </div>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                Professionele, uniforme e-mailhandtekeningen voor iedereen in je bedrijf. Centraal beheerd, dus altijd consistent en up-to-date —
                zonder dat medewerkers er zelf iets voor hoeven te doen.
              </p>
              <div className="mt-4 space-y-1.5">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-rinqel-green" />
                  <span className="text-xs text-muted-foreground">Centraal beheerd · Altijd consistent</span>
                </div>
                <p className="text-sm font-semibold">€ 1,35 <span className="text-xs font-normal text-muted-foreground">per gebruiker per maand</span></p>
                <p className="text-xs text-muted-foreground">Minimaal 10 gebruikers per Microsoft-omgeving</p>
              </div>
              <Button variant="link" className="mt-3 justify-start px-0" asChild>
                <Link to="/diensten/werkplek/emailhandtekeningen">
                  Meer over e-mailhandtekeningen
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sectie 6 — Altijd inclusief */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Altijd <span className="text-gradient">inclusief</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Bij Rinqel zijn alle diensten altijd inclusief. Geen verrassingen op je factuur, geen kleine lettertjes. Gewoon alles wat je nodig hebt — voor één vaste prijs.
            </p>
          </motion.div>

          <div className="mx-auto mt-10 grid max-w-3xl gap-4 sm:grid-cols-2">
            {[
              {
                icon: Ban,
                title: "Geen uurtje-factuurtje",
                text: "Je betaalt een vast bedrag per maand. Geen verborgen kosten, nooit een rekening achteraf.",
              },
              {
                icon: HeadphonesIcon,
                title: "Onbeperkt support",
                text: "Bel zo vaak als je wilt. Je krijgt direct een technisch specialist aan de lijn — geen wachtrij, geen ticketsysteem.",
              },
              {
                icon: BadgeCheck,
                title: "Geen verrassingen",
                text: "Wat je ziet, is wat je betaalt. Eerlijk, transparant en voorspelbaar. Elke maand hetzelfde bedrag.",
              },
              {
                icon: FileCheck,
                title: "Geen kleine lettertjes",
                text: "Maandelijks opzegbaar. Geen langlopende verplichtingen, geen boeteclausules. Wij vertrouwen op kwaliteit.",
              },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="flex items-start gap-3 rounded-xl border border-border bg-card p-5 shadow-card"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-hero-gradient text-primary-foreground">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold">{item.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sectie 7 — CTA */}
      <section className="bg-muted/40 py-12">
        <div className="container">
          <motion.div
            className="mx-auto flex max-w-3xl flex-col items-center gap-4 text-center"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl font-bold md:text-2xl">
              Benieuwd wat de digitale werkplek voor jouw bedrijf kan betekenen?
            </h3>
            <p className="text-muted-foreground">
              We denken graag met je mee. Geen verkooppraatje, gewoon een goed gesprek.
            </p>
            <Button variant="hero" size="lg" asChild>
              <Link to="/contact">
                Neem contact op
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default WerkplekContent;
